import React, { useCallback, useEffect, useState } from 'react';
import { req } from '../services/api';
import Modal from '../components/Modal';

const PAGE_SIZE = 25;

const STATUS_LABELS = {
  PENDING: 'Čeká',
  AUCTION: 'Aukce',
  ACCEPTED: 'Přijato',
  DRIVER_EN_ROUTE: 'Řidič na cestě',
  IN_PROGRESS: 'Probíhá',
  COMPLETED: 'Dokončeno',
  CANCELLED: 'Zrušeno'
};

const STATUS_BADGES = {
  PENDING: 'badge-gray',
  AUCTION: 'badge-gold',
  ACCEPTED: 'badge-green',
  DRIVER_EN_ROUTE: 'badge-green',
  IN_PROGRESS: 'badge-green',
  COMPLETED: 'badge-gray',
  CANCELLED: 'badge-red'
};

const CATEGORY_LABELS = {
  STANDARD: 'Standard',
  BUSINESS: 'Business',
  VIP: 'VIP',
  SUV: 'SUV',
  FAMILY: 'Family',
  LIMO: 'Limuzína'
};

const formatDateTime = value => {
  if (!value) return '—';
  return new Date(value).toLocaleString('cs-CZ');
};

const money = value => {
  if (value === null || value === undefined) return '—';
  return Number(value).toFixed(2) + ' €';
};

const rideCode = ride =>
  ride?.id ? '#ET-' + ride.id.slice(-6).toUpperCase() : '—';

const shortAddress = value => {
  if (!value) return '—';
  return String(value).split(',')[0];
};

const jsonText = value => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const escapeHtml = value =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

export default function Rides() {
  const [rides, setRides] = useState([]);
  const [stats, setStats] = useState(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [status, setStatus] = useState('');
  const [provider, setProvider] = useState('');
  const [category, setCategory] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [detailModal, setDetailModal] = useState(false);
  const [currentRide, setCurrentRide] = useState(null);

  const [chatModal, setChatModal] = useState(false);
  const [messages, setMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let active = true;

    req('GET', '/stats')
      .then(data => {
        if (active && data) {
          setStats(data);
        }
      })
      .catch(error => {
        console.error('Rides stats load failed:', error);
      });

    return () => {
      active = false;
    };
  }, []);

  const loadRides = useCallback(async () => {
    setLoading(true);

    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(PAGE_SIZE)
      });
  
      if (debouncedSearch) {
        params.set('search', debouncedSearch);
      }
  
      if (status) {
        params.set('status', status);
      }
  
      if (provider) {
        params.set('provider', provider);
      }
  
      if (category) {
        params.set('category', category);
      }
  
      if (dateFrom) {
        params.set('dateFrom', dateFrom);
      }
  
      if (dateTo) {
        params.set('dateTo', dateTo);
      }
  
      const data = await req(
        'GET',
        '/rides?' + params.toString()
      );
  
      if (data && Array.isArray(data.rides)) {
        setRides(data.rides);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
      } else {
        setRides([]);
        setTotal(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Rides load failed:', error);
      setRides([]);
      setTotal(0);
      setTotalPages(1);

      alert(
        'Chyba p\u0159i na\u010d\u00edt\u00e1n\u00ed j\u00edzd: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    } finally {
      setLoading(false);
    }
  }, [
    page,
    debouncedSearch,
    status,
    provider,
    category,
    dateFrom,
    dateTo
  ]);

  useEffect(() => {
    loadRides();
  }, [loadRides]);

  const resetFilters = () => {
    setSearch('');
    setStatus('');
    setProvider('');
    setCategory('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const providerName = ride => {
    if (ride.providerType === 'FLEET') {
      return ride.fleet?.companyName || 'Flotila';
    }

    if (ride.providerType === 'DRIVER') {
      return ride.driver?.user?.name || 'Řidič';
    }

    return 'Nepřiřazeno';
  };

  const providerSecondary = ride => {
    if (
      ride.providerType === 'FLEET' &&
      ride.driver?.user?.name
    ) {
      return 'Řidič: ' + ride.driver.user.name;
    }

    if (
      ride.providerType === 'DRIVER' &&
      ride.driver?.companyName
    ) {
      return ride.driver.companyName;
    }

    return null;
  };

  const forceStart = async id => {
    if (
      !window.confirm(
        'Opravdu chcete administr\u00e1torsky zah\u00e1jit tuto j\u00edzdu bez ohledu na polohu \u0159idi\u010de?'
      )
    ) {
      return;
    }

    try {
      await req('POST', '/rides/' + id + '/force-start', {});
      setDetailModal(false);
      await loadRides();
      alert('\u2713 J\u00edzda byla administr\u00e1torsky zah\u00e1jena');
    } catch (error) {
      alert(
        'Chyba p\u0159i zah\u00e1jen\u00ed j\u00edzdy: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    }
  };

  const forceComplete = async id => {
    if (
      !window.confirm(
        'Opravdu chcete administr\u00e1torsky dokon\u010dit tuto j\u00edzdu?'
      )
    ) {
      return;
    }

    try {
      await req(
        'POST',
        '/rides/' + id + '/force-complete',
        {}
      );

      setDetailModal(false);
      await loadRides();
      alert('\u2713 J\u00edzda byla administr\u00e1torsky dokon\u010dena');
    } catch (error) {
      alert(
        'Chyba p\u0159i dokon\u010den\u00ed j\u00edzdy: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    }
  };

  const cancelRide = async id => {
    if (
      !window.confirm(
        'Opravdu chcete administr\u00e1torsky zru\u0161it tuto j\u00edzdu?'
      )
    ) {
      return;
    }

    try {
      await req('PATCH', '/rides/' + id + '/cancel', {});

      setDetailModal(false);
      await loadRides();

      alert('\u2713 J\u00edzda byla zru\u0161ena');
    } catch (error) {
      alert(
        'Chyba p\u0159i ru\u0161en\u00ed j\u00edzdy: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    }
  };

  const showRoute = ride => {
    let points = [];

    try {
      points = JSON.parse(ride.routePoints || '[]');
    } catch {}

    const uniquePoints = Array.isArray(points)
      ? points.filter(
          (point, index) =>
            index === 0 ||
            point.lat !== points[index - 1].lat ||
            point.lng !== points[index - 1].lng
        )
      : [];

    let mapPoints = uniquePoints;

    if (mapPoints.length < 2) {
      if (
        ride.actualStartLat != null &&
        ride.actualStartLng != null &&
        ride.actualEndLat != null &&
        ride.actualEndLng != null
      ) {
        mapPoints = [
          {
            lat: ride.actualStartLat,
            lng: ride.actualStartLng
          },
          {
            lat: ride.actualEndLat,
            lng: ride.actualEndLng
          }
        ];
      } else {
        alert(
          'Pro tuto jízdu zatím nejsou k dispozici GPS data trasy.'
        );
        return;
      }
    }

    const apiKey =
      process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    if (!apiKey) {
      alert(
        'Google Maps API klíč není nakonfigurován.'
      );
      return;
    }

    const w = window.open();

    if (!w) {
      alert(
        'Prohlížeč zablokoval otevření mapy.'
      );
      return;
    }

    const safeStart = escapeHtml(
      ride.actualStartAddress || 'Nezaznamenáno'
    );

    const safeEnd = escapeHtml(
      ride.actualEndAddress || 'Nezaznamenáno'
    );

    const pointJson = JSON.stringify(
      mapPoints.map(point => ({
        lat: Number(point.lat),
        lng: Number(point.lng)
      }))
    ).replace(/</g, '\\u003c');

    const html = [
      '<!doctype html>',
      '<html><head><meta charset="utf-8">',
      '<title>Trasa jízdy</title>',
      '<style>',
      'body{margin:0;background:#111;color:#fff;font-family:Arial,sans-serif;}',
      '#header{padding:16px 20px;}',
      '#header h3{margin:0;color:#C8A84B;}',
      '#map{width:100%;height:calc(100vh - 170px);}',
      '.info{padding:12px 20px;font-size:14px;}',
      '.info p{margin:5px 0;}',
      '</style></head><body>',
      '<div id="header"><h3>Trasa ' +
        escapeHtml(rideCode(ride)) +
        '</h3></div>',
      '<div id="map"></div>',
      '<div class="info">',
      '<p><strong>Skutečný start:</strong> ' +
        safeStart +
        '</p>',
      '<p><strong>Skutečný cíl:</strong> ' +
        safeEnd +
        '</p>',
      '<p style="color:#888;">GPS bodů: ' +
        mapPoints.length +
        '</p>',
      '</div>',
      '<script>',
      'function initMap(){',
      'const points=' + pointJson + ';',
      'const map=new google.maps.Map(document.getElementById("map"),{zoom:14,center:points[Math.floor(points.length/2)]});',
      'const path=new google.maps.Polyline({path:points,geodesic:true,strokeColor:"#C8A84B",strokeOpacity:1,strokeWeight:4});',
      'path.setMap(map);',
      'new google.maps.Marker({position:points[0],map:map,label:"S",title:"Start"});',
      'new google.maps.Marker({position:points[points.length-1],map:map,label:"C",title:"Cíl"});',
      'const bounds=new google.maps.LatLngBounds();',
      'points.forEach(function(point){bounds.extend(point);});',
      'map.fitBounds(bounds);',
      '}',
      '</script>',
      '<script src="https://maps.googleapis.com/maps/api/js?key=' +
        encodeURIComponent(apiKey) +
        '&callback=initMap" async defer></script>',
      '</body></html>'
    ].join('');

    w.document.write(html);
    w.document.close();
  };

  const openChat = async ride => {
    setCurrentRide(ride);
    setMessages([]);
    setChatLoading(true);
    setChatModal(true);

    try {
      const data = await req(
        'GET',
        '/rides/' + ride.id + '/messages'
      );

      setMessages(Array.isArray(data) ? data : []);
    } catch (error) {
      setChatModal(false);
      alert(
        'Chyba p\u0159i na\u010d\u00edt\u00e1n\u00ed chatu: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    } finally {
      setChatLoading(false);
    }
  };

  const openDetail = ride => {
    setCurrentRide(ride);
    setDetailModal(true);
  };

  const setFilterValue = (setter, value) => {
    setter(value);
    setPage(1);
  };

  return (
    <div>
      <div className="page-title">Jízdy</div>

      <div className="page-sub">
        Kompletní provozní přehled všech jízd v systému
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">
            {stats?.rides ?? '—'}
          </div>
          <div className="stat-label">
            Celkem jízd
          </div>
        </div>

        <div className="stat-card active">
          <div className="stat-value">
            {stats?.activeRides ?? '—'}
          </div>
          <div className="stat-label">
            Aktivní jízdy
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-value">
            {stats?.completedRides ?? '—'}
          </div>
          <div className="stat-label">
            Dokončeno
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-value">
            {stats?.cancelledRides ?? '—'}
          </div>
          <div className="stat-label">
            Zrušeno
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-value">
            {money(stats?.revenue)}
          </div>
          <div className="stat-label">
            Hodnota dokončených jízd
          </div>
        </div>
      </div>

      <div className="admin-toolbar">
        <input
          className="search-bar"
          placeholder="Hledat kód, zákazníka, řidiče, flotilu, SPZ nebo adresu..."
          value={search}
          onChange={event =>
            setSearch(event.target.value)
          }
        />

        <select
          value={status}
          onChange={event =>
            setFilterValue(
              setStatus,
              event.target.value
            )
          }
        >
          <option value="">Všechny stavy</option>
          <option value="PENDING">Čeká</option>
          <option value="AUCTION">Aukce</option>
          <option value="ACCEPTED">Přijato</option>
          <option value="DRIVER_EN_ROUTE">
            Řidič na cestě
          </option>
          <option value="IN_PROGRESS">
            Probíhá
          </option>
          <option value="COMPLETED">
            Dokončeno
          </option>
          <option value="CANCELLED">
            Zrušeno
          </option>
        </select>

        <select
          value={provider}
          onChange={event =>
            setFilterValue(
              setProvider,
              event.target.value
            )
          }
        >
          <option value="">
            Všichni poskytovatelé
          </option>
          <option value="driver">
            Samostatný řidič
          </option>
          <option value="fleet">
            Flotila
          </option>
          <option value="unassigned">
            Nepřiřazeno
          </option>
        </select>

        <select
          value={category}
          onChange={event =>
            setFilterValue(
              setCategory,
              event.target.value
            )
          }
        >
          <option value="">
            Všechny kategorie
          </option>
          <option value="STANDARD">Standard</option>
          <option value="BUSINESS">Business</option>
          <option value="VIP">VIP</option>
          <option value="SUV">SUV</option>
          <option value="FAMILY">Family</option>
          <option value="LIMO">Limuzína</option>
        </select>

        <input
          type="date"
          value={dateFrom}
          onChange={event =>
            setFilterValue(
              setDateFrom,
              event.target.value
            )
          }
          title="Jízda od"
        />

        <input
          type="date"
          value={dateTo}
          onChange={event =>
            setFilterValue(
              setDateTo,
              event.target.value
            )
          }
          title="Jízda do"
        />

        <button
          className="btn-sm btn-gray"
          onClick={resetFilters}
        >
          Zrušit filtry
        </button>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">
            Přehled jízd
          </span>

          <span className="dashboard-muted">
            {total} výsledků
          </span>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Kód</th>
                <th>Zákazník</th>
                <th>Poskytovatel</th>
                <th>Trasa</th>
                <th>Termín</th>
                <th>Kategorie</th>
                <th>Cena</th>
                <th>Status</th>
                <th>Aktivita</th>
                <th>Akce</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="10"
                    className="loading-cell"
                  >
                    Načítám...
                  </td>
                </tr>
              ) : rides.length === 0 ? (
                <tr>
                  <td
                    colSpan="10"
                    className="empty-cell"
                  >
                    Žádné jízdy odpovídající filtrům
                  </td>
                </tr>
              ) : (
                rides.map(ride => (
                  <tr key={ride.id}>
                    <td>
                      <strong className="gold-text">
                        {rideCode(ride)}
                      </strong>
                      <br />
                      <span className="table-secondary">
                        {formatDateTime(
                          ride.createdAt
                        )}
                      </span>
                    </td>

                    <td>
                      <strong>
                        {ride.customer?.user?.name ||
                          '—'}
                      </strong>
                      <br />
                      <span className="table-secondary">
                        {ride.customer?.user?.email ||
                          '—'}
                      </span>
                    </td>

                    <td>
                      <strong>
                        {providerName(ride)}
                      </strong>

                      {providerSecondary(ride) && (
                        <>
                          <br />
                          <span className="table-secondary">
                            {providerSecondary(ride)}
                          </span>
                        </>
                      )}

                      <br />

                      <span className="table-secondary">
                        {ride.providerType === 'FLEET'
                          ? 'Flotila'
                          : ride.providerType ===
                              'DRIVER'
                            ? 'Samostatný řidič'
                            : 'Bez poskytovatele'}
                      </span>
                    </td>

                    <td>
                      <strong>
                        {shortAddress(
                          ride.fromAddress
                        )}
                      </strong>

                      <br />

                      <span className="table-secondary">
                        →
                      </span>

                      <br />

                      <strong>
                        {shortAddress(
                          ride.toAddress
                        )}
                      </strong>

                      <br />

                      <span className="table-secondary">
                        {Number(
                          ride.distanceKm || 0
                        ).toFixed(1)} km ·{' '}
                        {ride.durationMin || 0} min
                      </span>
                    </td>

                    <td>
                      {formatDateTime(
                        ride.scheduledAt
                      )}
                    </td>

                    <td>
                      {CATEGORY_LABELS[
                        ride.vehicleCategory
                      ] || ride.vehicleCategory}
                    </td>

                    <td>
                      <strong className="gold-text">
                        {money(ride.finalPrice)}
                      </strong>

                      {ride.finalPrice == null && (
                        <>
                          <br />
                          <span className="table-secondary">
                            max.{' '}
                            {money(ride.maxPrice)}
                          </span>
                        </>
                      )}
                    </td>

                    <td>
                      <span
                        className={
                          'badge ' +
                          (STATUS_BADGES[
                            ride.status
                          ] || 'badge-gray')
                        }
                      >
                        {STATUS_LABELS[
                          ride.status
                        ] || ride.status}
                      </span>
                    </td>

                    <td>
                      <span className="table-secondary">
                        Nabídky:{' '}
                        {ride._count?.bids || 0}
                      </span>
                      <br />
                      <span className="table-secondary">
                        Zprávy:{' '}
                        {ride._count?.messages || 0}
                      </span>
                    </td>

                    <td>
                      <div className="actions">
                        <button
                          className="btn-sm btn-blue"
                          onClick={() =>
                            openDetail(ride)
                          }
                        >
                          Detail
                        </button>

                        <button
                          className="btn-sm btn-blue"
                          onClick={() =>
                            openChat(ride)
                          }
                        >
                          Chat
                        </button>

                        <button
                          className="btn-sm btn-gold"
                          onClick={() =>
                            showRoute(ride)
                          }
                        >
                          Trasa
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && totalPages > 1 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 12,
              padding: '16px 0'
            }}
          >
            <button
              className="btn-sm btn-gray"
              disabled={page === 1}
              onClick={() =>
                setPage(current =>
                  Math.max(1, current - 1)
                )
              }
              style={{
                opacity: page === 1 ? 0.4 : 1
              }}
            >
              ← Předchozí
            </button>

            <span className="table-secondary">
              Stránka {page} z {totalPages}
            </span>

            <button
              className="btn-sm btn-gray"
              disabled={page === totalPages}
              onClick={() =>
                setPage(current =>
                  Math.min(
                    totalPages,
                    current + 1
                  )
                )
              }
              style={{
                opacity:
                  page === totalPages ? 0.4 : 1
              }}
            >
              Další →
            </button>
          </div>
        )}
      </div>

      {detailModal && currentRide && (
        <Modal
          title={'Detail jízdy ' + rideCode(currentRide)}
          onClose={() => setDetailModal(false)}
        >
          <div className="payout-detail-box">
            <div className="payout-row">
              <span className="payout-row-label">
                Stav
              </span>
              <span className="payout-row-value">
                {STATUS_LABELS[
                  currentRide.status
                ] || currentRide.status}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Vytvořeno
              </span>
              <span className="payout-row-value">
                {formatDateTime(
                  currentRide.createdAt
                )}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Naplánováno
              </span>
              <span className="payout-row-value">
                {formatDateTime(
                  currentRide.scheduledAt
                )}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Dokončeno
              </span>
              <span className="payout-row-value">
                {formatDateTime(
                  currentRide.completedAt
                )}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Zákazník
              </span>
              <span className="payout-row-value">
                {currentRide.customer?.user
                  ?.name || '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                E-mail zákazníka
              </span>
              <span className="payout-row-value">
                {currentRide.customer?.user
                  ?.email || '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Telefon zákazníka
              </span>
              <span className="payout-row-value">
                {currentRide.customer?.user
                  ?.phone || '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Poskytovatel
              </span>
              <span className="payout-row-value">
                {providerName(currentRide)}
              </span>
            </div>

            {currentRide.fleet && (
              <div className="payout-row">
                <span className="payout-row-label">
                  Flotila
                </span>
                <span className="payout-row-value">
                  {currentRide.fleet.companyName}
                  {currentRide.fleet.ico
                    ? ' · IČO ' +
                      currentRide.fleet.ico
                    : ''}
                </span>
              </div>
            )}

            {currentRide.driver && (
              <>
                <div className="payout-row">
                  <span className="payout-row-label">
                    Řidič
                  </span>
                  <span className="payout-row-value">
                    {currentRide.driver.user
                      ?.name || '—'}
                  </span>
                </div>

                <div className="payout-row">
                  <span className="payout-row-label">
                    Typ řidiče
                  </span>
                  <span className="payout-row-value">
                    {currentRide.driver.user
                      ?.role === 'FLEET_DRIVER'
                      ? 'Řidič flotily'
                      : 'Samostatný řidič'}
                  </span>
                </div>
              </>
            )}

            <div className="payout-row">
              <span className="payout-row-label">
                Trasa
              </span>
              <span className="payout-row-value">
                {currentRide.fromAddress}
                <br />
                → {currentRide.toAddress}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Vzdálenost / čas
              </span>
              <span className="payout-row-value">
                {Number(
                  currentRide.distanceKm || 0
                ).toFixed(1)} km ·{' '}
                {currentRide.durationMin || 0} min
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Kategorie
              </span>
              <span className="payout-row-value">
                {CATEGORY_LABELS[
                  currentRide.vehicleCategory
                ] || currentRide.vehicleCategory}
              </span>
            </div>

            {currentRide.vehicle && (
              <>
                <div className="payout-row">
                  <span className="payout-row-label">
                    Vozidlo
                  </span>
                  <span className="payout-row-value">
                    {currentRide.vehicle.brand}{' '}
                    ({currentRide.vehicle.year})
                    {' · '}
                    {currentRide.vehicle.plate}
                  </span>
                </div>

                <div className="payout-row">
                  <span className="payout-row-label">
                    Barva / místa
                  </span>
                  <span className="payout-row-value">
                    {currentRide.vehicle.color ||
                      '—'}
                    {' · '}
                    {currentRide.vehicle.seats ||
                      '—'} míst
                  </span>
                </div>
              </>
            )}

            <div className="payout-row">
              <span className="payout-row-label">
                Cestující
              </span>
              <span className="payout-row-value">
                {currentRide.passengerCount || 0}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Zavazadla
              </span>
              <span className="payout-row-value">
                malá:{' '}
                {currentRide.smallLuggageCount ||
                  0}
                {' · '}velká:{' '}
                {currentRide.largeLuggageCount ||
                  0}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Číslo letu
              </span>
              <span className="payout-row-value">
                {currentRide.flightNumber || '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Maximální cena
              </span>
              <span className="payout-row-value">
                {money(currentRide.maxPrice)}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Konečná cena
              </span>
              <span className="payout-row-value gold-text">
                {money(currentRide.finalPrice)}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Provize
              </span>
              <span className="payout-row-value">
                {currentRide.commissionRate !=
                null
                  ? Number(
                      currentRide.commissionRate
                    ).toFixed(1) + ' %'
                  : '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Provize částka
              </span>
              <span className="payout-row-value">
                {money(
                  currentRide.commissionAmount
                )}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Čistá částka poskytovatele
              </span>
              <span className="payout-row-value">
                {money(
                  currentRide.providerNetAmount
                )}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Nabídky
              </span>
              <span className="payout-row-value">
                {currentRide._count?.bids || 0}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Zprávy
              </span>
              <span className="payout-row-value">
                {currentRide._count?.messages ||
                  0}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Hodnocení
              </span>
              <span className="payout-row-value">
                {currentRide.rating
                  ? currentRide.rating + ' / 5'
                  : '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Komentář
              </span>
              <span className="payout-row-value">
                {currentRide.comment || '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Skutečný start
              </span>
              <span className="payout-row-value">
                {currentRide.actualStartAddress ||
                  '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Skutečný konec
              </span>
              <span className="payout-row-value">
                {currentRide.actualEndAddress ||
                  '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Speciální požadavky
              </span>
              <span
                className="payout-row-value"
                style={{
                  whiteSpace: 'pre-wrap'
                }}
              >
                {jsonText(
                  currentRide.specialRequirements
                )}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Poznámka
              </span>
              <span
                className="payout-row-value"
                style={{
                  whiteSpace: 'pre-wrap'
                }}
              >
                {currentRide.specialNotes || '—'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Mezizastávky
              </span>
              <span
                className="payout-row-value"
                style={{
                  whiteSpace: 'pre-wrap'
                }}
              >
                {jsonText(
                  currentRide.extraStops
                )}
              </span>
            </div>
          </div>

          <div className="actions">
            <button
              className="btn-sm btn-blue"
              onClick={() =>
                openChat(currentRide)
              }
            >
              Chat
            </button>

            <button
              className="btn-sm btn-gold"
              onClick={() =>
                showRoute(currentRide)
              }
            >
              Trasa
            </button>

            {[
              'ACCEPTED',
              'DRIVER_EN_ROUTE'
            ].includes(currentRide.status) && (
              <button
                className="btn-sm btn-green"
                onClick={() =>
                  forceStart(currentRide.id)
                }
              >
                Zahájit jízdu
              </button>
            )}

            {currentRide.status ===
              'IN_PROGRESS' && (
              <button
                className="btn-sm btn-green"
                onClick={() =>
                  forceComplete(
                    currentRide.id
                  )
                }
              >
                Dokončit jízdu
              </button>
            )}

            {![
              'CANCELLED',
              'COMPLETED'
            ].includes(currentRide.status) && (
              <button
                className="btn-sm btn-red"
                onClick={() =>
                  cancelRide(currentRide.id)
                }
              >
                Zrušit jízdu
              </button>
            )}
          </div>
        </Modal>
      )}

      {chatModal && currentRide && (
        <Modal
          title={
            'Chat jízdy ' +
            rideCode(currentRide)
          }
          onClose={() => setChatModal(false)}
        >
          <div className="chat-box">
            {chatLoading ? (
              <div className="loading-cell">
                Načítám zprávy...
              </div>
            ) : messages.length === 0 ? (
              <div className="empty-cell">
                Žádné zprávy
              </div>
            ) : (
              messages.map((message, index) => (
                <div
                  key={message.id || index}
                  className="chat-msg"
                >
                  <div className="chat-msg-header">
                    {message.sender?.name ||
                      message.senderRole ||
                      'Neznámý'}
                    {' · '}
                    {formatDateTime(
                      message.createdAt
                    )}
                  </div>

                  <div className="chat-msg-body">
                    {message.text || ''}
                  </div>
                </div>
              ))
            )}
          </div>

          <button
            className="btn-gray"
            style={{ width: '100%' }}
            onClick={() =>
              setChatModal(false)
            }
          >
            Zavřít
          </button>
        </Modal>
      )}
    </div>
  );
}
